import { Text, UseDisclosureReturn, VStack } from "@chakra-ui/react";
import { AccountAccessRole } from "@syla/shared/types/models/AccountAccessBase";
import React from "react";
import { useForm } from "react-hook-form";
import isEmail from "validator/lib/isEmail";
import { InputField } from "../../components/forms/InputField";
import { SingleSelectorField } from "../../components/forms/SingleSelectorField";
import { FormModal } from "../../components/modals/FormModal";
import { useCurrentAccountStore } from "../../store/currentAccountStore";
import { useMutationInviteAccountMember } from "../../store/useMutationInviteAccountMember";
import { AccountAccessRoleOptions } from "./AccountAccessRoleSelector";

type InviteMemberFormValues = {
  email: string;
  role: AccountAccessRole;
};

export function InviteMemberModal({
  disclosure,
}: {
  disclosure: UseDisclosureReturn;
}): React.ReactElement {
  const accountId = useCurrentAccountStore(({ accountId }) => accountId);

  const form = useForm<InviteMemberFormValues>({
    mode: "onTouched",
    defaultValues: {
      email: "",
      role: AccountAccessRole.Accountant,
    },
  });

  const mutationInviteAccountMember = useMutationInviteAccountMember(accountId);

  const onSubmit = async ({ email, role }: InviteMemberFormValues) => {
    await mutationInviteAccountMember.mutateAsync({
      members: [{ role, email }],
    });
  };

  return (
    <FormModal
      disclosure={disclosure}
      form={form}
      title="Invite account member"
      submitButtonText="Send invite"
      successTitle="Invite sent"
      errorTitle="Failed to invite member"
      onSubmit={onSubmit}
    >
      <Text color="gray.600" mb={6}>
        Invite someone to access this account. They’ll receive an email with
        instructions to join.
      </Text>

      <VStack spacing={6}>
        <InputField
          form={form}
          name="email"
          label="Email"
          type="email"
          placeholder="Enter their email address"
          helperText="We’ll send the invite to this email address."
          isRequired
          autoFocus
          rules={{
            validate: (value) =>
              isEmail(value) || "Enter a valid email address",
          }}
        />

        <SingleSelectorField
          form={form}
          name="role"
          label="Role"
          options={AccountAccessRoleOptions}
          helperText="Choose what this person can view or change in the account."
          isRequired
          buttonProps={{
            width: "full",
          }}
          popoverProps={{
            matchWidth: true,
          }}
        />
      </VStack>
    </FormModal>
  );
}
